/**
 * De bronnen van één stelling: toevoegen (tekst of bestand), per categorie
 * bekijken, een bron lezen, bewerken en verwijderen.
 *
 * PRO en CONTRA zijn de bronnen voor de kennis-personages en hebben een
 * woordlimiet; PRO_EXPERT en CONTRA_EXPERT gaan door de embeddings en
 * mogen langer zijn.
 */
import React, { useState } from 'react';
import { Plus, Save, Trash2, FileText, Edit2 } from 'lucide-react';
import { kleur } from '../../../theme/tokens';
import { basis } from '../stijlen';

export const MAX_WOORDEN_BASIS = 1000;
export const TYPES_MET_LIMIET = ['PRO', 'CONTRA'];
const CATEGORIEEN = ['PRO', 'CONTRA', 'PRO_EXPERT', 'CONTRA_EXPERT'];
const LEGE_BRON = { naam: '', inhoud: '', type: 'PRO', titel_voor_leerlingen: '' };

export const telWoorden = (tekst) => (tekst ? tekst.trim().split(/\s+/).filter(w => w).length : 0);

function Woordteller({ tekst, lang }) {
  const aantal = telWoorden(tekst);
  const teLang = aantal > MAX_WOORDEN_BASIS;
  return (
    <div style={{ ...styles.woordTeller, color: teLang ? kleur.rood : kleur.stil }}>
      {aantal} / {MAX_WOORDEN_BASIS} woorden
      {teLang && <span style={styles.woordWarning}> - Te lang!{lang ? ' Gebruik PRO_EXPERT of CONTRA_EXPERT voor langere teksten.' : ''}</span>}
    </div>
  );
}

export function BronnenBeheer({ api, stelling, onHerlaad }) {
  const [toonForm, setToonForm] = useState(false);
  // null: tekst invoeren; 'selecteer': bestand kiezen; een File: gekozen.
  const [bestand, setBestand] = useState(null);
  const [nieuw, setNieuw] = useState(LEGE_BRON);
  const [categorie, setCategorie] = useState(null);
  const [bron, setBron] = useState(null);
  const [bewerkt, setBewerkt] = useState(null);

  const voegToe = async (e) => {
    e.preventDefault();
    try {
      let data;
      if (bestand && bestand !== 'selecteer') {
        data = await api.uploadBron(stelling.id, bestand, nieuw.type, nieuw.titel_voor_leerlingen);
        if (!data.success) { alert(data.detail || 'Fout bij uploaden'); return; }
      } else {
        data = await api.maakTekstbron(stelling.id, nieuw);
        if (!data.success) return;
      }
      setNieuw(LEGE_BRON); setBestand(null); setToonForm(false);
      await onHerlaad();
    } catch (err) { console.error('Fout:', err); }
  };

  const bekijk = async (bronId) => {
    setBewerkt(null);
    try {
      const data = await api.haalBron(bronId);
      if (data.success) setBron(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const slaOp = async () => {
    try {
      const data = await api.wijzigBron(bewerkt.id, bewerkt.naam, bewerkt.inhoud);
      if (data.success) { setBewerkt(null); setBron(data.data); await onHerlaad(); }
    } catch (err) { console.error('Fout:', err); }
  };

  const verwijder = async (bronId) => {
    if (!window.confirm('Weet je zeker?')) return;
    try {
      const data = await api.verwijderBron(bronId);
      if (data.success) await onHerlaad();
    } catch (err) { console.error('Fout:', err); }
  };

  const bronnenIn = (cat) => stelling.sources?.[cat] || [];

  return (
    <>
      <div style={styles.panelHeader}>
        <h3 style={styles.subheader}>Bronnen</h3>
        <button onClick={() => setToonForm(true)} style={styles.addBtn}><Plus size={16} /> Bron</button>
      </div>
      {toonForm && (
        <form onSubmit={voegToe} style={styles.form}>
          <div style={styles.formTabs}>
            <button type="button" onClick={() => setBestand(null)} style={!bestand ? styles.formTabActive : styles.formTab}>Tekst invoeren</button>
            <button type="button" onClick={() => setBestand('selecteer')} style={bestand ? styles.formTabActive : styles.formTab}>Bestand uploaden</button>
          </div>
          {!bestand ? (
            <>
              <input type="text" placeholder="Naam van de bron" value={nieuw.naam} onChange={(e) => setNieuw({ ...nieuw, naam: e.target.value })} style={styles.input} required />
              <textarea placeholder="Inhoud" value={nieuw.inhoud} onChange={(e) => setNieuw({ ...nieuw, inhoud: e.target.value })} style={{ ...styles.input, minHeight: '100px' }} required />
              {TYPES_MET_LIMIET.includes(nieuw.type) && <Woordteller tekst={nieuw.inhoud} lang />}
            </>
          ) : (
            <>
              <input type="file" accept=".pdf,.docx" onChange={(e) => setBestand(e.target.files[0])} style={styles.input} required />
              <input type="text" placeholder="Titel voor leerlingen" value={nieuw.titel_voor_leerlingen} onChange={(e) => setNieuw({ ...nieuw, titel_voor_leerlingen: e.target.value })} style={styles.input} required />
            </>
          )}
          <select value={nieuw.type} onChange={(e) => setNieuw({ ...nieuw, type: e.target.value })} style={styles.input}>
            <option value="PRO">PRO</option>
            <option value="CONTRA">CONTRA</option>
            <option value="PRO_EXPERT">PRO EXPERT</option>
            <option value="CONTRA_EXPERT">CONTRA EXPERT</option>
          </select>
          <div style={styles.formButtons}>
            <button type="submit" style={styles.saveBtn}><Save size={16} /> Toevoegen</button>
            <button type="button" onClick={() => { setToonForm(false); setBestand(null); }} style={styles.cancelBtn}>Annuleren</button>
          </div>
        </form>
      )}

      <div style={styles.bronnenGrid}>
        {CATEGORIEEN.map(cat => (
          <div key={cat} style={styles.bronnenColumn} onClick={() => setCategorie(cat)}>
            <h4 style={styles.bronCategoryHeader}>{cat.replace('_', ' ')} <span style={styles.bronCount}>({bronnenIn(cat).length})</span></h4>
            <p style={styles.bronPreviewText}>Klik om bronnen te bekijken</p>
          </div>
        ))}
      </div>

      {categorie && (
        <div style={styles.categoryModal}>
          <div style={styles.categoryModalHeader}>
            <h3>{categorie.replace('_', ' ')} bronnen</h3>
            <button onClick={() => { setCategorie(null); setBron(null); setBewerkt(null); }} style={styles.closeBtn}>×</button>
          </div>
          <div style={styles.categoryBronList}>
            {bronnenIn(categorie).length > 0 ? bronnenIn(categorie).map(b => (
              <div key={b.id} style={{ ...styles.categoryBronItem, ...(bron?.id === b.id ? styles.categoryBronItemActive : {}) }}>
                <div style={styles.categoryBronHeader} onClick={() => bekijk(b.id)}>
                  <span style={styles.categoryBronNaam}>{b.naam}</span>
                  <button onClick={(e) => { e.stopPropagation(); verwijder(b.id); }} style={styles.deleteBtn}><Trash2 size={14} /></button>
                </div>
              </div>
            )) : <p style={styles.empty}>Geen bronnen in deze categorie</p>}
          </div>

          {bron && (
            <div style={styles.bronDetailPanel}>
              {bewerkt ? (
                <>
                  <input type="text" value={bewerkt.naam} onChange={(e) => setBewerkt({ ...bewerkt, naam: e.target.value })} style={styles.input} placeholder="Naam" />
                  <textarea value={bewerkt.inhoud} onChange={(e) => setBewerkt({ ...bewerkt, inhoud: e.target.value })} style={{ ...styles.input, minHeight: '300px', fontFamily: 'inherit' }} />
                  {TYPES_MET_LIMIET.includes(bewerkt.type) && <Woordteller tekst={bewerkt.inhoud} />}
                  <div style={styles.formButtons}>
                    <button onClick={slaOp} style={styles.saveBtn}
                      disabled={TYPES_MET_LIMIET.includes(bewerkt.type) && telWoorden(bewerkt.inhoud) > MAX_WOORDEN_BASIS}>
                      <Save size={16} /> Opslaan
                    </button>
                    <button onClick={() => setBewerkt(null)} style={styles.cancelBtn}>Annuleren</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={styles.bronDetailHeader}>
                    <h4>{bron.naam}</h4>
                    {!bron.bestandsnaam && (
                      <button onClick={() => setBewerkt({ ...bron })} style={styles.editBtn}><Edit2 size={14} /> Bewerken</button>
                    )}
                  </div>
                  {bron.bestandsnaam && <p style={styles.bronInfo}><FileText size={14} /> Bestand: {bron.bestandsnaam}</p>}
                  {bron.titel_voor_leerlingen && <p style={styles.bronInfo}>Titel voor leerlingen: {bron.titel_voor_leerlingen}</p>}
                  <pre style={styles.bronTekst}>{bron.inhoud}</pre>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

const styles = {
  ...basis,
  formTabs: { display: 'flex', gap: '8px', marginBottom: '16px' },
  formTab: { flex: 1, padding: '10px', background: kleur.lijn, border: 'none', borderRadius: '6px', cursor: 'pointer' },
  formTabActive: { flex: 1, padding: '10px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  woordTeller: { fontSize: '13px', marginTop: '4px', marginBottom: '8px' },
  woordWarning: { fontWeight: 'bold' },
  bronnenGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' },
  bronnenColumn: { background: kleur.vlak, borderRadius: '8px', padding: '16px', cursor: 'pointer', transition: 'all 0.2s', border: '2px solid transparent' },
  bronCategoryHeader: { margin: 0, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' },
  bronCount: { background: kleur.accent, color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'normal' },
  bronPreviewText: { margin: 0, fontSize: '12px', color: kleur.stil },
  categoryModal: { marginTop: '20px', padding: '20px', background: 'white', border: `1px solid ${kleur.lijn}`, borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  categoryModalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: `1px solid ${kleur.lijn}`, paddingBottom: '12px' },
  categoryBronList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  categoryBronItem: { background: kleur.vlak, borderRadius: '6px', padding: '12px', cursor: 'pointer', border: '2px solid transparent' },
  categoryBronItemActive: { border: `2px solid ${kleur.accent}`, background: kleur.accentLichter },
  categoryBronHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  categoryBronNaam: { fontWeight: 'bold', color: kleur.blauw },
  bronDetailPanel: { marginTop: '16px', padding: '16px', background: kleur.vlak, borderRadius: '8px', borderTop: `2px solid ${kleur.accent}` },
  bronDetailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  editBtn: { padding: '6px 12px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' },
  closeBtn: { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: kleur.stil },
  bronTekst: { background: 'white', padding: '16px', borderRadius: '6px', whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '14px', maxHeight: '400px', overflow: 'auto', border: `1px solid ${kleur.lijn}` },
};
